import React from "react";
import Button from "Components/DesignSystem/Button/Button";
import Typography from "Components/DesignSystem/Typography/Typography";

/**
 * The file-picker card shared by `Create`, `Update` and `Validate` (§ 126).
 *
 * All three rendered a byte-identical block — a bordered drop area wrapping a hidden
 * `<input type="file">`, the "(Note: Only .xlsx files are allowed to upload)" line,
 * the selected filename, and a Delete / Cancel / Save footer. Only the labels and
 * the handlers differed.
 *
 * **Strictly presentational.** Not one line of upload, polling, permission or CBM
 * logic moved in here: the three callers keep `do_upload_pdw`, `checkUploadStatus`,
 * their `setTimeout` recursion and their `RulesContext` coordination exactly where
 * they were. This owns markup and nothing else, which is what makes the change safe
 * for the Rules CBM commit flow that two of the three feed.
 *
 * Two fixes came with it:
 *
 * - **The upload icon rendered as nothing.** It was `<i className="fa fa-cloud-upload
 *   fa-x">`, and that glyph does not resolve in this app — every screenshot of these
 *   three modals shows a tall empty box above the note text. It is an inline SVG now.
 * - **`id="fupload"` was hardcoded in all three** (and in `BulkDeleteXLS`), so any two
 *   mounted together produced duplicate DOM ids. `inputId` is required.
 */
const UploadCloudIcon = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M16 16l-4-4-4 4" />
    <path d="M12 12v9" />
    <path d="M20.39 18.39A5 5 0 0018 9h-1.26A8 8 0 103 16.3" />
    <path d="M16 16l-4-4-4 4" />
  </svg>
);

const PdwUploadDropzone = ({
  inputId,
  inputTestId,
  inputKey,
  onFileSelect,
  selectedFile,
  onDelete,
  onCancel,
  onSave,
  cancelLabel = "Cancel",
  saveLabel = "Save",
  saveDisabled,
  cancelTestId,
  saveTestId,
  deleteTestId,
}) => (
  <div>
    <div className="tw-border tw-border-edge tw-rounded-srf tw-p-8">
      <label
        htmlFor={inputId}
        className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-text-center tw-cursor-pointer tw-text-accent"
      >
        <input
          data-testid={inputTestId}
          type="file"
          id={inputId}
          className="tw-sr-only"
          key={inputKey || ""}
          onChange={onFileSelect}
          /* Clearing the value on click is what lets the SAME file be re-picked
             after a failed upload — without it the input fires no change event. */
          onClick={(event) => (event.target.value = null)}
        />
        <UploadCloudIcon />
        <Typography variant="body-primary" as="span" className="tw-text-ink">
          (Note: Only .xlsx files are allowed to upload)
        </Typography>
        <Typography className="tw-text-ink" variant="title-header" as="span">
          Browse File
        </Typography>
      </label>

      {selectedFile && (
        <div className="tw-mt-3 tw-text-center tw-break-words">
          <Typography variant="body-primary" as="span" className="tw-text-ink">
            {selectedFile.name}
          </Typography>
        </div>
      )}
    </div>

    {/*
      Delete far left, the dismiss/commit pair right — the layout these three modals
      already shipped, and the § 122 grammar the Data Model tab settled on. Delete is
      `danger` rather than the legacy `btn btn-outline-danger`.
    */}
    <div className="tw-mt-4 tw-flex tw-items-center tw-justify-between tw-gap-3">
      <Button
        variant="danger"
        data-testid={deleteTestId}
        disabled={!selectedFile}
        onClick={onDelete}
      >
        Delete
      </Button>

      <div className="tw-flex tw-items-center tw-gap-3">
        <Button variant="secondary" data-testid={cancelTestId} onClick={onCancel}>
          {cancelLabel}
        </Button>
        <Button
          variant="primary"
          data-testid={saveTestId}
          disabled={saveDisabled}
          onClick={onSave}
        >
          {saveLabel}
        </Button>
      </div>
    </div>
  </div>
);

export default PdwUploadDropzone;
